import { useEffect } from 'react';
import type { CoverEditorState } from '../doc/coverEditorReducer';
import type { CoverEditorDispatch } from '../doc/useCoverEditorDoc';
import { newCoverItemId } from '../doc/coverIds';
import { nudgeStep } from './boxGestures';

/** True when the key press belongs to a field that the user types in. */
function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
}

const ARROWS: Readonly<Record<string, [number, number]>> = {
  ArrowLeft: [-1, 0],
  ArrowRight: [1, 0],
  ArrowUp: [0, -1],
  ArrowDown: [0, 1],
};

/**
 * The keys of the editor: Cmd or Ctrl + Z is Undo, with Shift (or Ctrl + Y) Redo; with a text box
 * selected, the arrows move it (Shift: a larger step), Delete removes it, Cmd or Ctrl + D copies
 * it; Escape clears the selection. No key acts while the user types in a field.
 */
export function useEditorKeys(state: CoverEditorState, dispatch: CoverEditorDispatch, enabled: boolean): void {
  const { selection, doc } = state;
  useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (isTyping(event.target)) {
        return;
      }
      const isCommand = event.metaKey || event.ctrlKey;
      const key = event.key.toLowerCase();
      if (isCommand && key === 'z') {
        event.preventDefault();
        dispatch({ type: event.shiftKey ? 'redo' : 'undo' });
        return;
      }
      if (isCommand && key === 'y') {
        event.preventDefault();
        dispatch({ type: 'redo' });
        return;
      }
      if (event.key === 'Escape') {
        dispatch({ type: 'select', selection: null });
        return;
      }
      const box = selection?.kind === 'text' ? doc.texts.find((text) => text.id === selection.id) : undefined;
      if (box === undefined) {
        if (selection?.kind === 'effect' && (event.key === 'Delete' || event.key === 'Backspace')) {
          event.preventDefault();
          dispatch({ type: 'removeEffect', id: selection.id });
        }
        return;
      }
      const arrow = ARROWS[event.key];
      if (arrow !== undefined) {
        event.preventDefault();
        const step = nudgeStep(event.shiftKey);
        dispatch({
          type: 'updateText',
          id: box.id,
          patch: { x: box.x + arrow[0] * step, y: box.y + arrow[1] * step },
          coalesce: `text:${box.id}:nudge`,
        });
        return;
      }
      if (event.key === 'Delete' || event.key === 'Backspace') {
        event.preventDefault();
        dispatch({ type: 'removeText', id: box.id });
        return;
      }
      if (isCommand && key === 'd') {
        event.preventDefault();
        dispatch({ type: 'duplicateText', id: box.id, newId: newCoverItemId('t') });
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [dispatch, doc.texts, enabled, selection]);
}
